import { useMemo, useState } from 'react';
import { getAllTags, getTagGroups, getTag } from '../../shared/tags/tagDictionary';

export default function TagFilterPanel({
  appliesTo,
  selectedTags,
  onChange,
  onClear,
  matchMode = 'or',
  onMatchModeChange,
}) {
  const [openGroups, setOpenGroups] = useState(() => new Set());

  const groups = useMemo(() => getTagGroups(appliesTo), [appliesTo]);
  const allTags = useMemo(() => getAllTags(appliesTo), [appliesTo]);

  const selectedSet = useMemo(
    () => new Set((selectedTags || []).map(Number)),
    [selectedTags]
  );

  const tagsByGroup = useMemo(() => {
    const map = {};
    for (const group of groups) map[group] = [];
    for (const tag of allTags) {
      if (!map[tag.group]) map[tag.group] = [];
      map[tag.group].push(tag);
    }
    return map;
  }, [allTags, groups]);

  const toggleGroup = (group) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const toggleTag = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next].sort((a, b) => a - b));
  };

  const removeTag = (id) => {
    onChange(selectedTags.filter((t) => Number(t) !== Number(id)));
  };

  return (
    <div className="gs-tag-filter">
      <div className="gs-tag-filter__header">
        <span className="gs-field-label">Tags</span>
        {selectedSet.size > 0 && (
          <button type="button" className="gs-btn gs-btn--ghost gs-tag-filter__clear" onClick={onClear}>
            Clear tags ({selectedSet.size})
          </button>
        )}
      </div>

      {selectedSet.size > 0 && (
        <>
          <div className="gs-tag-filter__selected" aria-label="Selected tags">
            {[...selectedSet].map((id) => {
              const def = getTag(id);
              if (!def) return null;
              return (
                <button
                  key={id}
                  type="button"
                  className="gs-tag-chip gs-tag-chip--selected"
                  onClick={() => removeTag(id)}
                  title={`Remove ${def.en}`}
                >
                  {def.en}
                  <span className="gs-tag-chip__x" aria-hidden="true">
                    ×
                  </span>
                </button>
              );
            })}
          </div>
          <p className="gs-tag-filter__sentence" role="group" aria-label="Tag match mode">
            Show results with{' '}
            <button
              type="button"
              className={`gs-tag-filter__mode-btn${matchMode === 'or' ? ' is-active' : ''}`}
              aria-pressed={matchMode === 'or'}
              onClick={() => onMatchModeChange?.('or')}
            >
              any
            </button>
            {' / '}
            <button
              type="button"
              className={`gs-tag-filter__mode-btn${matchMode === 'and' ? ' is-active' : ''}`}
              aria-pressed={matchMode === 'and'}
              onClick={() => onMatchModeChange?.('and')}
            >
              all
            </button>
            {' '}
            of these tags
          </p>
        </>
      )}

      <div className="gs-tag-filter__groups">
        {groups.map((group) => {
          const tags = tagsByGroup[group] || [];
          if (!tags.length) return null;
          const isOpen = openGroups.has(group);
          const selectedInGroup = tags.filter((t) => selectedSet.has(t.id)).length;
          return (
            <div key={group} className={`gs-tag-filter__group${isOpen ? ' is-open' : ''}`}>
              <div
                className="gs-tag-filter__group-toggle"
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={() => toggleGroup(group)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleGroup(group);
                  }
                }}
              >
                <span className="gs-tag-filter__group-name">{group}</span>
                <span className="gs-tag-filter__group-meta">
                  {selectedInGroup > 0 ? (
                    <span className="gs-tag-filter__group-count">{selectedInGroup} selected</span>
                  ) : null}
                  <span className="gs-tag-filter__chevron" aria-hidden="true">
                    {isOpen ? '▾' : '▸'}
                  </span>
                </span>
              </div>
              {isOpen && (
                <div className="filterButtons gs-tag-filter__chips">
                  {tags.map((tag) => {
                    const active = selectedSet.has(tag.id);
                    return (
                      <label
                        key={tag.id}
                        className={active ? 'gs-chip is-active' : 'gs-chip'}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleTag(tag.id)}
                        />
                        {tag.en}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
